import { useNotificationStore } from '@/composables/useNotificationStore';
import { AxiosError } from 'axios';
import { createCancelableRequest } from 'src/services/api';
import type { ApiConfig } from 'src/services/apiMap';
import { onBeforeUnmount, reactive } from 'vue';

const generateRandomString = (): string =>
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
        const random = (Math.random() * 16) | 0;
        const value = char === 'x' ? random : (random & 0x3) | 0x8;
        return value.toString(16);
    });

type APICallHistory = {
    endpoint: string;
    event: ApiConfig;
    timeStamp: string;
    tracer?: string;
};

type CancelFunction = () => boolean;
const cachedRequest: Record<string, CancelFunction | undefined> = {};

export const useApi = () => {
    const notificationStore = useNotificationStore();
    const traceLogs = reactive<Record<string, APICallHistory>>({});
    const { axiosInstance } = createCancelableRequest();

    const makeRequest = async ({ url, method, params, data }: ApiConfig) => {
        const requestId = generateRandomString();
        const controller = new AbortController();
        const signal = controller.signal;

        traceLogs[requestId] = {
            endpoint: url,
            event: { url, data, params, method },
            timeStamp: new Date().toISOString()
        };
        cachedRequest[requestId] = () => {
            try {
                controller.abort();
                delete cachedRequest[requestId];
                return true;
            } catch (error) {
                console.error('Error canceling request:', error);
                return false;
            }
        };
        try {
            const response = await axiosInstance.request({
                url,
                method,
                data,
                params,
                signal
            });
            return response.data;
        } catch (error: unknown) {
            console.log('API Request Error:', { error, trace: traceLogs[requestId] });
            if (error instanceof AxiosError && error.response) {
                notificationStore.showErrorNetMessage((error as AxiosError).response?.data);
            } else if ((error as AxiosError).code === 'ERR_NETWORK') {
                notificationStore.showErrorNetMessage({ code: 'offline' });
            }
            if ((error as AxiosError).name === 'CanceledError') {
                console.warn('Request was canceled');
            } else {
                console.error('Request error:', error);
            }
        }
    };

    const cancelSpecificRequest = async (requestId: string): Promise<boolean> => {
        const cancelFn = cachedRequest[requestId];
        if (cancelFn) {
            const result = await cancelFn();
            delete cachedRequest[requestId];
            return result;
        }
        return false;
    };

    const cancelAllRequests = async () => {
        Object.keys(cachedRequest).forEach(async (requestId) => {
            await cancelSpecificRequest(requestId);
        });
    };
    onBeforeUnmount(async () => {
        Object.keys(cachedRequest).forEach(async (requestId) => {
            const cancelFn = cachedRequest[requestId];
            if (cancelFn) {
                await cancelFn();
                // delete cachedRequest[requestId];
            }
        });
    });
    return {
        makeRequest,
        cancelSpecificRequest,
        cancelAllRequests,
        traceLogs
    };
};
